import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import './../../../Auth.css';
import AuthLeftBranding from './AuthLeftBranding';
import AuthLandingFooter from './AuthLandingFooter';
import AuthBackToHome from './AuthBackToHome';
import useNoFocusZoomOnIOS from '../../../Utils/useNoFocusZoomOnIOS';

function ResetPassword() {
    useNoFocusZoomOnIOS();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' });
    const [loader, setLoader] = useState(false);
    const [isResetSuccess, setIsResetSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState({ password: false, confirm: false });
    const navigate = useNavigate();

    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleResetPassword = async () => {
        if (!token) {
            toast.error('Invalid or missing reset link. Please request a new password reset.');
            return;
        }

        if (!validateForm()) return;

        setLoader(true);
        try {
            const resetData = {
                token,
                password,
                confirmPassword,
            };

            const response = await postApi('/auth/reset-password', resetData);

            if (response?.statusCode === 200) {
                setIsResetSuccess(true);
                toast.success(response?.message || 'Password reset successfully.');
                setErrors({ password: '', confirmPassword: '' });
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                toast.error(response?.message || 'Failed to reset password. Please try again.');
            }
        } catch (error) {
            console.error('Reset password error:', error);
            toast.error(
                error?.response?.data?.message ||
                    error?.data?.message ||
                    'Failed to reset password. The link may have expired.'
            );
        } finally {
            setLoader(false);
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        if (!password.trim()) {
            validationErrors.password = 'Password is required.';
        } else if (password.length < 6) {
            validationErrors.password = 'Password must be at least 6 characters.';
        }
        if (!confirmPassword.trim()) {
            validationErrors.confirmPassword = 'Please confirm your password.';
        } else if (password !== confirmPassword) {
            validationErrors.confirmPassword = 'Passwords do not match.';
        }
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    return (
        <div className="login-main login-main--landing-footer">
            <div className="login-main-split login-change">
                <div className="left-div">
                    <AuthBackToHome placement="bluePanel" />
                    <AuthLeftBranding />
                </div>

                <div className="login-container">
                    <h2>Reset Password</h2>

                    {!token ? (
                        <div className="success-message">
                            <h4>Invalid Reset Link</h4>
                            <p>This password reset link is invalid or has expired.</p>
                            <div className="signup-bottom signup-bottom--login">
                                <Link to="/forgot-password" className="signup-link">
                                    Request a new link
                                </Link>
                                <span> · </span>
                                <Link to="/login" className="signup-link">
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    ) : isResetSuccess ? (
                        <div className="success-message">
                            <h4>Password Reset Successful</h4>
                            <p>Your password has been updated. Redirecting you to login...</p>
                            <div className="signup-bottom signup-bottom--login">
                                <Link to="/login" className="signup-link">
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="auth-page-subtitle">Enter your new password below.</p>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleResetPassword();
                                }}
                            >
                                <div className="form-group">
                                    <label htmlFor="password" className="required-label">
                                        New Password
                                    </label>
                                    <div className="password-wrapper">
                                        <input
                                            id="password"
                                            type={showPassword.password ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="form-control"
                                        />
                                        <span
                                            className="eye-icon"
                                            onClick={() => togglePasswordVisibility('password')}
                                        >
                                            <img
                                                src={
                                                    showPassword.password
                                                        ? window.getPublicUrl('/assets/images/Eye.svg')
                                                        : window.getPublicUrl('/assets/images/Eye-closed.svg')
                                                }
                                                alt="Toggle Password"
                                            />
                                        </span>
                                    </div>
                                    {errors.password && <div className="error">{errors.password}</div>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmPassword" className="required-label">
                                        Confirm Password
                                    </label>
                                    <div className="password-wrapper">
                                        <input
                                            id="confirmPassword"
                                            type={showPassword.confirm ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            className="form-control"
                                        />
                                        <span
                                            className="eye-icon"
                                            onClick={() => togglePasswordVisibility('confirm')}
                                        >
                                            <img
                                                src={
                                                    showPassword.confirm
                                                        ? window.getPublicUrl('/assets/images/Eye.svg')
                                                        : window.getPublicUrl('/assets/images/Eye-closed.svg')
                                                }
                                                alt="Toggle Password"
                                            />
                                        </span>
                                    </div>
                                    {errors.confirmPassword && (
                                        <div className="error">{errors.confirmPassword}</div>
                                    )}
                                </div>

                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="login-btn"
                                    disabled={loader}
                                >
                                    {loader && (
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only"></span>
                                        </div>
                                    )}
                                    Reset Password
                                </Button>
                            </form>

                            <div className="signup-bottom signup-bottom--login">
                                <span>Remember your password? </span>
                                <Link to="/login" className="signup-link">
                                    Back to Login
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AuthLandingFooter />
        </div>
    );
}

export default ResetPassword;
